var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Dimensions, Easing, PanResponder, Platform, } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import styled from 'styled-components/native';
import Skeleton from '@/components/Skeleton';
import { SccPressable } from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import useNavigation from '@/navigation/useNavigation';
import { openAppDeepLink } from '@/utils/appLinkNavigation';
import { useCheckAuth } from '@/utils/checkAuth';
import { isAppDeepLink } from '@/utils/deepLinkUtils';
const AUTO_SCROLL_INTERVAL_MS = 5000;
const INITIAL_DELAY_MS = 1500;
const SCROLL_ANIMATION_DURATION_MS = 800;
const SCREEN_WIDTH = Dimensions.get('window').width;
const BANNER_HORIZONTAL_PADDING = 20;
const BANNER_WIDTH = SCREEN_WIDTH - BANNER_HORIZONTAL_PADDING * 2;
// 390pt 디자인 기준 띠배너 박스는 350×67(≈5.22:1). 고정 높이를 쓰면 화면 폭이
// 디자인과 다를 때(웹 프레임/태블릿 등) cover 크롭이 심해진다 → 폭에 비례해 높이를
// 계산해 어디서나 디자인 비율을 유지한다. (390pt 폭에선 기존과 동일한 67)
const BANNER_HEIGHT = Math.round(BANNER_WIDTH * (67 / 350));
const BANNER_GAP = 12;
const ITEM_SLOT_WIDTH = BANNER_WIDTH + BANNER_GAP;
const WINDOW_HALF = 10;
export default function StripBannerSection({ banners, onPanStateChange, isLoading, }) {
    const len = banners.length;
    const scrollPosition = useRef(new Animated.Value(0)).current;
    const scrollPosRef = useRef(0);
    const renderCenterRef = useRef(0);
    const [renderCenter, setRenderCenter] = useState(0);
    const [displayIndex, setDisplayIndex] = useState(0);
    const autoScrollTimer = useRef(null);
    const panStartRef = useRef(0);
    const onPanStateChangeRef = useRef(onPanStateChange);
    onPanStateChangeRef.current = onPanStateChange;
    useEffect(() => {
        const id = scrollPosition.addListener(({ value }) => {
            scrollPosRef.current = value;
        });
        return () => scrollPosition.removeListener(id);
    }, [scrollPosition]);
    const items = useMemo(() => {
        if (len <= 1) {
            return banners.map((b, i) => ({ banner: b, ringIndex: i, slot: i }));
        }
        return Array.from({ length: WINDOW_HALF * 2 + 1 }, (_, i) => {
            const slot = renderCenter - WINDOW_HALF + i;
            const ringIdx = ((slot % len) + len) % len;
            return { banner: banners[ringIdx], ringIndex: ringIdx, slot };
        });
    }, [banners, renderCenter, len]);
    const updateCenter = useCallback((newCenter) => {
        renderCenterRef.current = newCenter;
        setRenderCenter(newCenter);
        setDisplayIndex(((newCenter % len) + len) % len);
    }, [len]);
    // ── Auto scroll ──────────────────────────────────────────────
    const startAutoScroll = useCallback(() => {
        if (len <= 1) {
            return;
        }
        autoScrollTimer.current = setInterval(() => {
            const currentSlot = Math.round(-scrollPosRef.current / ITEM_SLOT_WIDTH);
            const targetPos = -(currentSlot + 1) * ITEM_SLOT_WIDTH;
            Animated.timing(scrollPosition, {
                toValue: targetPos,
                duration: SCROLL_ANIMATION_DURATION_MS,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }).start(({ finished }) => {
                if (!finished) {
                    return;
                }
                updateCenter(currentSlot + 1);
            });
        }, AUTO_SCROLL_INTERVAL_MS);
    }, [len, scrollPosition, updateCenter]);
    const stopAutoScroll = useCallback(() => {
        if (autoScrollTimer.current) {
            clearInterval(autoScrollTimer.current);
            autoScrollTimer.current = null;
        }
        scrollPosition.stopAnimation();
    }, [scrollPosition]);
    const isFocused = useIsFocused();
    useEffect(() => {
        if (len <= 1) {
            return;
        }
        if (!isFocused) {
            stopAutoScroll();
            return;
        }
        // Start with initial delay to avoid simultaneous rolling with main banner
        const initialTimer = setTimeout(() => {
            startAutoScroll();
        }, INITIAL_DELAY_MS);
        return () => {
            clearTimeout(initialTimer);
            stopAutoScroll();
        };
    }, [isFocused, len, startAutoScroll, stopAutoScroll]);
    // ── Manual swipe via PanResponder ────────────────────────────
    const panResponder = useMemo(() => PanResponder.create({
        onMoveShouldSetPanResponderCapture: (_, { dx, dy }) => Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10,
        onPanResponderGrant: () => {
            var _a;
            (_a = onPanStateChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPanStateChangeRef, true);
            stopAutoScroll();
            panStartRef.current = scrollPosRef.current;
        },
        onPanResponderMove: (_, { dx }) => {
            const newPos = panStartRef.current + dx;
            scrollPosition.setValue(newPos);
            const newCenter = Math.round(-newPos / ITEM_SLOT_WIDTH);
            if (Math.abs(newCenter - renderCenterRef.current) >
                Math.floor(WINDOW_HALF / 2)) {
                updateCenter(newCenter);
            }
        },
        onPanResponderRelease: (_, { dx, vx }) => {
            var _a;
            (_a = onPanStateChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPanStateChangeRef, false);
            const startSlot = Math.round(-panStartRef.current / ITEM_SLOT_WIDTH);
            let targetSlot = startSlot;
            if (Math.abs(dx) > BANNER_WIDTH / 4 || Math.abs(vx) > 0.5) {
                targetSlot = dx > 0 ? startSlot - 1 : startSlot + 1;
            }
            const targetPos = -targetSlot * ITEM_SLOT_WIDTH;
            Animated.spring(scrollPosition, {
                toValue: targetPos,
                useNativeDriver: true,
                overshootClamping: true,
                restSpeedThreshold: 0.1,
                restDisplacementThreshold: 0.1,
            }).start(({ finished }) => {
                if (!finished) {
                    return;
                }
                scrollPosition.setValue(targetPos);
                scrollPosRef.current = targetPos;
                updateCenter(targetSlot);
                startAutoScroll();
            });
        },
        onPanResponderTerminate: () => {
            var _a;
            (_a = onPanStateChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onPanStateChangeRef, false);
            const currentSlot = Math.round(-scrollPosRef.current / ITEM_SLOT_WIDTH);
            const targetPos = -currentSlot * ITEM_SLOT_WIDTH;
            scrollPosition.setValue(targetPos);
            scrollPosRef.current = targetPos;
            updateCenter(currentSlot);
            startAutoScroll();
        },
        onPanResponderTerminationRequest: () => false,
    }), [scrollPosition, stopAutoScroll, startAutoScroll, updateCenter]);
    // ── Render ───────────────────────────────────────────────────
    if (len === 0) {
        if (isLoading) {
            return (_jsx(Container, { children: _jsx(SkeletonWrapper, { children: _jsx(Skeleton, { style: {
                            width: BANNER_WIDTH,
                            height: BANNER_HEIGHT,
                            borderRadius: 6,
                        } }) }) }));
        }
        return null;
    }
    if (len === 1) {
        return (_jsx(LogParamsProvider, { params: { displaySectionName: 'strip_banner_section' }, children: _jsx(Container, { children: _jsx(SingleBannerWrapper, { children: _jsx(StripBanner, { banner: banners[0], index: 0, trackView: true }) }) }) }));
    }
    return (_jsx(LogParamsProvider, { params: { displaySectionName: 'strip_banner_section' }, children: _jsxs(Container, { children: [_jsx(BannerTrack, Object.assign({}, panResponder.panHandlers, { children: _jsx(Animated.View, { style: { transform: [{ translateX: scrollPosition }] }, children: items.map(item => (_jsx(BannerSlot, { style: {
                                left: item.slot * ITEM_SLOT_WIDTH + BANNER_HORIZONTAL_PADDING,
                            }, children: _jsx(StripBanner, { banner: item.banner, index: item.ringIndex, trackView: item.slot === renderCenter }) }, item.slot))) }) })), _jsx(DotsContainer, { children: banners.map((_, index) => (_jsx(Dot, { active: index === displayIndex }, index))) })] }) }));
}
function StripBanner({ banner, index, trackView = false }) {
    const navigation = useNavigation();
    const checkAuth = useCheckAuth();
    const openBanner = () => __awaiter(this, void 0, void 0, function* () {
        const url = banner.clickPageUrl;
        if (isAppDeepLink(url)) {
            openAppDeepLink(url, navigation);
        }
        else {
            navigation.navigate('Webview', {
                fixedTitle: banner.clickPageTitle,
                url: url,
            });
        }
    });
    return (_jsx(SccPressable, { elementName: "home_v2_strip_banner", logParams: { banner_key: banner.loggingKey, index }, trackView: trackView, onPress: () => 
        // 웹: 배너는 비회원도 그냥 볼 수 있게 authGuard 없이 바로 연다.
        Platform.OS === 'web' ? openBanner() : checkAuth(openBanner), children: _jsx(BannerContainer, { children: _jsx(SccRemoteImage, { imageUrl: banner.imageUrl, style: {
                    width: BANNER_WIDTH,
                    height: BANNER_HEIGHT,
                    borderRadius: 6,
                } }) }) }));
}
// ── Styles ───────────────────────────────────────────────────────
const Container = styled.View `
  padding-bottom: 32px;
`;
const SingleBannerWrapper = styled.View `
  padding-horizontal: ${BANNER_HORIZONTAL_PADDING}px;
`;
const BannerTrack = styled.View `
  height: ${BANNER_HEIGHT}px;
  overflow: hidden;
`;
const BannerSlot = styled.View `
  position: absolute;
  width: ${BANNER_WIDTH}px;
  height: ${BANNER_HEIGHT}px;
  top: 0;
`;
const BannerContainer = styled.View `
  border-radius: 6px;
  overflow: hidden;
`;
const DotsContainer = styled.View `
  flex-direction: row;
  gap: 6px;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
`;
const Dot = styled.View `
  width: 6px;
  height: 6px;
  border-radius: 100px;
  background-color: ${({ active }) => (active ? color.gray50v2 : '#e3e4e8')};
`;
const SkeletonWrapper = styled.View `
  padding-horizontal: ${BANNER_HORIZONTAL_PADDING}px;
`;
